import { FC, useEffect } from "react";
import ActionGroups from "./ActionGroups";
import ActionsPreview from "./ActionsPreview";
import { Box, Divider, Grid, lighten, Stack, Typography, useTheme } from "@mui/material";
import { Button } from "@gisce/oficina-virtual-components";
import { grey } from "@mui/material/colors";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HomeConfig } from "../../types/home";

const Title: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return <>
    <Grid container spacing={1} justifyContent="center">

      <Grid item>
        <Typography variant="h3" textAlign="center" display="inline-block">
          {t("common:text.home_landing_header_beginning")}
        </Typography>
      </Grid>

      <Grid item>
        <Typography
          variant="h3"
          textAlign="center"
          sx={{
            backgroundImage: `linear-gradient(217deg, ${lighten(theme.palette.primary.main, 0.5)} 0%, ${theme.palette.primary.main} 65%);`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}
          display="inline-block">
            {t("common:text.home_landing_header_ending")}
        </Typography>
      </Grid>

    </Grid>
    
    <Typography variant="h5" textAlign="center">
      {t("common:text.home_landing_subheader")}
    </Typography>
  </>
}

const InitButtons = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();  
  const { pathname } = useLocation();

  return <>
    <Button 
      variant="contained" 
      color="primary" 
      onClick={() => navigate(`${pathname}#actions`)}
    >
      {t("common:text.home_landing_header_begin_button")}
    </Button>
    {t("common:text.or")}
    <Button 
      variant="contained" 
      color="primary"
      onClick={() => navigate(t('common:url.login'))}
    >
      {t("common:text.home_landing_header_login_button")}
    </Button>
  </>
}

const Actions: FC<{showTopDivider: boolean}> = ({
  showTopDivider
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  return <>
    {!isAuthenticated && showTopDivider && <Divider 
        variant="middle" 
        sx={{
          mb: {xs: 2, sm: 6},
          pl: {md: "10vw", lg: "15vw"}, 
          pr: {md: "10vw", lg: "15vw"},
          color: grey[700]
        }}
      >
        {t("common:text.home_landing_header_all_actions_title")}
      </Divider>
    }
    <Box 
      sx={{
        mt: !showTopDivider ? {xs: 2, md: 4} : {}
      }}
    >
      <ActionGroups />
    </Box>
    
  </>
}

type TabGroupSettings = Extract<HomeConfig, { type: "tabGroups" }>;

const TabGroups: FC<{homeSettings: TabGroupSettings}> = ({homeSettings}) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        navigate(pathname)
      }
    }
  }, [hash]);

  return <Stack 
    spacing={8} 
    sx={{
      width: "100%", 
      pl: {xs: 2, md: 0},
      pr: {xs: 2, md: 0}
    }}
  >
    {!isAuthenticated && homeSettings.showHeroArea &&  <>
      <Box pt={8}>
        <Title />
      </Box>

      <Box display="flex" justifyContent="center" gap={2} alignItems="center">
        <InitButtons />
      </Box>

      <ActionsPreview />
    </>}

    <Box pt={isAuthenticated && 3} id="actions">
      <Actions showTopDivider={homeSettings.showHeroArea}/>
    </Box>
    
  </Stack>
}

export default TabGroups;